import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  IconButton,
} from "@mui/material";
import {
  MutableRefObject,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useChatSessionsContext } from "../lib/contexts/ChatSessionsContext";
import { useCustomThemeContext } from "../lib/contexts/ThemeContext";
import { useUserInfoContext } from "../lib/contexts/UserInfoContext";
import { ChatSession, SessionSearchResult } from "../lib/interfaces/Chat";
import { useNavigate } from "react-router-dom";
import { searchChatSessions } from "../lib/api/Chat";
import ChatSessionHistory from "./ChatSessionHistory";
import SearchResults from "./SearchResults";

interface DrawerItemsProps {
  handleDelete: (chatId: string) => void;
  handleRename: (chatId: string, newTitle: string) => void;
  deleteChatId: string;
  renameChatId: string;
  isLoading: MutableRefObject<boolean>;
}

export default function DrawerItems({
  handleDelete,
  handleRename,
  deleteChatId,
  renameChatId,
  isLoading,
}: DrawerItemsProps) {
  const { custom } = useCustomThemeContext();
  const { userInfo } = useUserInfoContext();
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
  });
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<SessionSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const {
    chatSessions,
    resetSelectedChatSession,
    handleDrawerClose,
    inputRef,
    invalidateActiveStream,
  } = useChatSessionsContext();

  let navigate = useNavigate();

  const performSearch = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        setSearchLoading(false);
        return;
      }

      if (!userInfo?.accessToken) {
        console.error("No access token available for search");
        return;
      }

      setSearchLoading(true);
      setIsSearching(true);

      try {
        const response = await searchChatSessions(
          query.trim(),
          20, // limit
          userInfo.accessToken,
          true // include highlights
        );
        console.log("Search response:", response); // Debug log

        if (response?.results) {
          setSearchResults(response.results);
        } else {
          console.log("No results in response"); // Debug log
          setSearchResults([]);
        }
      } catch (error) {
        console.error("Error searching chat sessions:", error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [userInfo]
  );

  // Debounced search functionality
  const debouncedSearch = useCallback(
    (query: string) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        performSearch(query);
      }, 500); // 500ms debounce delay
    },
    [performSearch]
  );

  const handleSearchChange = (event: any) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.trim()) {
      setIsSearching(true); // Set searching state immediately
      debouncedSearch(query);
    } else {
      setSearchResults([]);
      setIsSearching(false);
      setSearchLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setIsSearching(false);
    setSearchLoading(false);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
      });
    }
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      // Cleanup timeout on unmount
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const sessionsGroupedByMonths = chatSessions.reduce<
    Record<string, ChatSession[]>
  >((groupedSessions, chatSession) => {
    const month = chatSession.month;
    if (!groupedSessions[month]) {
      groupedSessions[month] = [];
    }
    groupedSessions[month].push(chatSession);
    return groupedSessions;
  }, {});

  return (
    <Box sx={{ marginLeft: "5%" }}>
      <Button
        variant="outlined"
        startIcon={<AddRoundedIcon />}
        sx={{
          width: "90%",
          marginTop: "5%",
          marginBottom: "3%",
        }}
        onClick={() => {
          invalidateActiveStream();
          resetSelectedChatSession();
          isLoading.current = false;
          handleDrawerClose();
          navigate("/chat");
          if (inputRef.current) {
            inputRef.current.focus(); // Focus the input element
          }
        }}
      >
        New Chat
      </Button>

      {/* Search Input Field */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search chat sessions..."
        value={searchQuery}
        onChange={handleSearchChange}
        sx={{
          width: "90%",
          marginBottom: "3%",
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: custom.palette.primary.light,
            },
            "&:hover fieldset": {
              borderColor: custom.palette.primary.main,
            },
            "&.Mui-focused fieldset": {
              borderColor: custom.palette.primary.main,
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {searchLoading ? (
                <CircularProgress
                  size={16}
                  sx={{ color: custom.palette.primary.light }}
                />
              ) : (
                <SearchIcon sx={{ color: custom.palette.primary.light }} />
              )}
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={clearSearch}
                sx={{ color: custom.palette.primary.light }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Display search results or default sessions */}
      {isSearching ? (
        <SearchResults
          searchResults={searchResults}
          searchQuery={searchQuery}
          searchLoading={searchLoading}
          isLoading={isLoading}
          windowSize={windowSize}
        />
      ) : (
        <ChatSessionHistory
          handleDelete={handleDelete}
          handleRename={handleRename}
          deleteChatId={deleteChatId}
          renameChatId={renameChatId}
          isLoading={isLoading}
          sessionsGroupedByMonths={sessionsGroupedByMonths}
          windowSize={windowSize}
        />
      )}
    </Box>
  );
}
